<template>
  <div class="creative-hub min-h-screen bg-[#050508] text-white flex overflow-hidden">
    <!-- 左侧窄栏 -->
    <aside
      class="w-[72px] shrink-0 border-r border-white/[0.06] bg-[#0a0a0c] flex flex-col items-center py-6 gap-6"
    >
      <router-link
        to="/home"
        class="w-11 h-11 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-violet-400 transition-colors"
        title="首页"
      >
        <i class="fas fa-home text-lg"></i>
      </router-link>
      <button
        type="button"
        class="w-11 h-11 rounded-xl text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
        title="项目"
      >
        <i class="fas fa-folder text-lg"></i>
      </button>
      <button
        type="button"
        class="w-11 h-11 rounded-xl text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
        title="社区"
      >
        <i class="fas fa-users text-lg"></i>
      </button>
      <div class="flex-1"></div>
      <button
        type="button"
        class="w-11 h-11 rounded-xl text-gray-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
        title="回收站"
      >
        <i class="fas fa-trash-alt text-lg"></i>
      </button>
    </aside>

    <div class="flex-1 flex flex-col min-w-0 min-h-screen">
      <!-- 顶栏 -->
      <header
        class="shrink-0 h-14 px-4 sm:px-6 flex items-center justify-between border-b border-white/[0.06] bg-[#050508]/95 backdrop-blur-md"
      >
        <div class="flex items-center gap-2">
          <span
            class="text-lg font-black tracking-tight bg-gradient-to-r from-fuchsia-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent"
          >
            OiiOii
          </span>
        </div>
        <div class="flex items-center gap-2 sm:gap-4 text-[11px] sm:text-xs text-gray-400">
          <button type="button" class="hidden sm:inline hover:text-white transition-colors">
            简体中文 <i class="fas fa-chevron-down ml-1 text-[9px]"></i>
          </button>
          <div class="hidden md:flex items-center gap-3">
            <button type="button" class="hover:text-green-400 transition-colors" title="微信">
              <i class="fab fa-weixin text-base"></i>
            </button>
            <button type="button" class="hover:text-indigo-400 transition-colors" title="Discord">
              <i class="fab fa-discord text-base"></i>
            </button>
          </div>
          <button type="button" class="hidden lg:inline hover:text-white transition-colors">
            常见问题
          </button>
          <button type="button" class="hover:text-white transition-colors">我的资产</button>
          <span
            class="px-2.5 py-1 rounded-full bg-gradient-to-r from-fuchsia-600/30 to-violet-600/30 border border-fuchsia-500/30 text-fuchsia-200 font-semibold"
          >
            100 FREE
          </span>
          <button
            type="button"
            class="w-9 h-9 rounded-full bg-white/[0.08] border border-white/10 flex items-center justify-center hover:bg-white/[0.12] transition-colors"
          >
            <i class="fas fa-user text-sm text-gray-300"></i>
          </button>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto scrollbar-thin">
        <!-- 顶部通知条 -->
        <transition name="fade">
          <div
            v-if="showTopBanner"
            class="mx-4 sm:mx-6 mt-4 rounded-xl bg-blue-600/20 border border-blue-500/30 px-4 py-2.5 flex items-center justify-between gap-3"
          >
            <p class="text-[11px] sm:text-xs text-blue-100/90 leading-relaxed">
              欢迎使用 OiiOii 创作中心：拖拽图片到输入区可尝试【角色】、【风格】参考。
            </p>
            <button
              type="button"
              @click="showTopBanner = false"
              class="shrink-0 w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-blue-200"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
        </transition>

        <div class="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-16">
          <!-- 中央大输入框 -->
          <div class="relative">
            <div
              class="relative rounded-2xl border border-white/[0.12] bg-[#12121a] shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] min-h-[220px] flex flex-col"
              :class="{ 'ring-2 ring-violet-500/40 border-violet-500/30': isDragging }"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="onDrop"
            >
              <!-- 右上角装饰（火焰小角色占位） -->
              <div
                class="absolute -top-3 right-6 w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-600 shadow-lg shadow-orange-500/30 flex items-center justify-center text-xl z-10 select-none pointer-events-none"
                aria-hidden="true"
              >
                🔥
              </div>

              <div class="flex-1 p-5 pt-8">
                <p class="text-sm text-gray-500 leading-relaxed mb-4">
                  拖拽/粘贴 图片到这里，来试试<span class="text-violet-300">【角色】</span>、<span
                    class="text-fuchsia-300"
                    >【风格】</span
                  >参考
                </p>
                <textarea
                  v-model="prompt"
                  rows="4"
                  class="w-full bg-transparent text-sm text-gray-200 placeholder-gray-600 resize-none outline-none leading-relaxed"
                  placeholder="描述你的创意，或粘贴文案…"
                  maxlength="5000"
                ></textarea>
              </div>

              <div class="px-4 pb-3 flex flex-wrap items-center gap-2">
                <span
                  class="text-[10px] px-2.5 py-1 rounded-full bg-white/[0.08] text-gray-400 border border-white/[0.06]"
                >
                  支持 .txt .docx .pdf .md，限5000字
                </span>
              </div>

              <div class="px-4 pb-4 flex items-end justify-between gap-3 border-t border-white/[0.06] pt-3">
                <div class="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    class="w-9 h-9 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-gray-300 transition-colors"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                  <button
                    type="button"
                    class="h-9 px-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-[11px] text-gray-300 flex items-center gap-2 transition-colors"
                  >
                    <i class="fas fa-scroll text-violet-400"></i>
                    剧本
                  </button>
                  <button
                    type="button"
                    class="h-9 px-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-[11px] text-gray-300 flex items-center gap-2 transition-colors"
                  >
                    <i class="fas fa-shapes text-cyan-400"></i>
                    149 种风格
                  </button>
                  <button
                    type="button"
                    class="h-9 px-3 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] text-[11px] text-gray-300 flex items-center gap-2 transition-colors"
                  >
                    <i class="far fa-smile text-amber-400"></i>
                    我的资产
                  </button>
                </div>
                <button
                  type="button"
                  @click="onSubmit"
                  class="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 shadow-lg shadow-violet-900/50 flex items-center justify-center text-white transition-all active:scale-95"
                  title="提交"
                >
                  <i class="fas fa-arrow-up"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- 功能 Tab -->
          <div class="mt-6 flex flex-wrap gap-2">
            <button
              v-for="tab in featureTabs"
              :key="tab.id"
              type="button"
              @click="onFeatureTabClick(tab)"
              class="px-4 py-2 rounded-full text-xs font-medium border transition-all flex items-center gap-2"
              :class="
                activeFeatureTab === tab.id
                  ? 'border-transparent bg-gradient-to-r from-fuchsia-600/40 to-violet-600/40 text-white shadow-[0_0_20px_rgba(168,85,247,0.25)]'
                  : 'border-white/[0.08] bg-white/[0.04] text-gray-400 hover:text-gray-200 hover:border-white/15'
              "
            >
              <i v-if="tab.star" class="fas fa-star text-amber-400 text-[10px]"></i>
              {{ tab.label }}
              <span
                v-if="tab.tag"
                class="text-[9px] px-1.5 py-0.5 rounded-md bg-white/10 text-gray-300"
                >{{ tab.tag }}</span
              >
            </button>
            <router-link
              :to="{ name: 'ConversationWorkflow' }"
              class="px-4 py-2 rounded-full text-xs font-medium border border-cyan-500/30 bg-cyan-600/15 text-cyan-100 hover:bg-cyan-600/25 transition-all flex items-center gap-2"
            >
              <i class="fas fa-project-diagram text-[10px]"></i>
              对话工作流
            </router-link>
          </div>

          <!-- 对话工作流记录（后端 chat_workflow_sessions） -->
          <section class="mt-12">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-base font-bold text-white flex items-center gap-2">
                <span class="text-amber-300">✨</span>
                对话工作流记录
              </h2>
              <router-link
                :to="{ name: 'ConversationWorkflow' }"
                class="text-xs text-gray-500 hover:text-violet-400 transition-colors flex items-center gap-1"
              >
                查看全部 <i class="fas fa-arrow-right text-[10px]"></i>
              </router-link>
            </div>
            <p v-if="workflowHistoryError" class="text-[11px] text-amber-200/90 mb-3">
              {{ workflowHistoryError }}
            </p>
            <div
              v-if="loadingWorkflowHistory"
              class="text-center text-gray-600 text-[11px] py-8"
            >
              加载记录中…
            </div>
            <div
              v-else-if="!workflowProjects.length"
              class="text-center text-gray-600 text-[11px] py-8 rounded-2xl border border-white/[0.06] bg-[#12121a]/50 leading-relaxed px-3"
            >
              暂无记录。进入「对话工作流」发送文案后会自动保存到此（需登录且后端已建表）。<br />
              <span class="text-gray-500">
                若库里已有数据仍不显示：请核对 <strong class="text-gray-400">user_id</strong> 是否与当前登录用户一致；并确认线上已部署
                <strong class="text-gray-400">GET /chat_workflow_sessions</strong> 与 Nginx 代理。
              </span>
            </div>
            <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <article
                v-for="p in workflowProjects"
                :key="p.uuid"
                role="button"
                tabindex="0"
                class="group rounded-2xl overflow-hidden bg-[#12121a] border border-white/[0.06] hover:border-violet-500/30 transition-all cursor-pointer"
                @click="openWorkflowSession(p)"
                @keydown.enter.prevent="openWorkflowSession(p)"
              >
                <div
                  class="aspect-[4/3] bg-gradient-to-br from-zinc-800/80 to-zinc-900 flex items-center justify-center relative overflow-hidden"
                >
                  <img
                    v-if="p.thumb"
                    :src="p.thumb"
                    alt=""
                    class="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  />
                  <span
                    v-else
                    class="text-[10px] text-white/20 font-black tracking-widest"
                  >
                    oiioii
                  </span>
                </div>
                <div class="p-3">
                  <p class="text-xs font-medium text-white truncate">{{ p.title }}</p>
                  <p class="text-[10px] text-gray-500 mt-1">{{ p.time }}</p>
                </div>
              </article>
            </div>
          </section>

          <!-- 亮点 -->
          <section class="mt-12">
            <h2 class="text-base font-bold text-white flex items-center gap-2 mb-4">
              <span class="text-amber-300">✨</span>
              亮点
            </h2>
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              <div
                v-for="n in 8"
                :key="n"
                class="aspect-square rounded-2xl bg-gradient-to-br from-violet-900/30 via-fuchsia-900/20 to-zinc-900 border border-white/[0.06] hover:border-fuchsia-500/25 transition-colors"
              ></div>
            </div>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { showSuccessToast, showErrorToast } from '@/utils/toast'
import { fetchChatWorkflowSessionList } from '@/utils/chatWorkflowHistoryApi'

export default {
  name: 'CreativeHub',
  data() {
    return {
      showTopBanner: true,
      isDragging: false,
      prompt: '',
      activeFeatureTab: 'seedance',
      featureTabs: [
        { id: 'seedance', label: 'Seedance 2.0 故事动画', star: true },
        { id: 'canvas', label: '自由画布', tag: '多模态' },
        { id: 'short', label: '剧情故事短片' },
        { id: 'char', label: '角色设计' }
      ],
      workflowProjects: [],
      loadingWorkflowHistory: false,
      workflowHistoryError: ''
    }
  },
  mounted() {
    window.addEventListener('paste', this.onPaste)
    this.loadWorkflowHistory()
  },
  watch: {
    '$route.name'(toName, fromName) {
      if (toName === 'CreativeHub' && fromName === 'ConversationWorkflow') {
        this.loadWorkflowHistory()
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('paste', this.onPaste)
  },
  methods: {
    async loadWorkflowHistory() {
      this.loadingWorkflowHistory = true
      this.workflowHistoryError = ''
      try {
        this.workflowProjects = await fetchChatWorkflowSessionList({ limit: 20 })
      } catch (e) {
        this.workflowProjects = []
        const status = e?.response?.status
        if (status === 401) {
          this.workflowHistoryError = '登录后可同步对话工作流记录（未携带有效 token）。'
        } else {
          const body = e?.response?.data
          const msg =
            (typeof body === 'string' ? body : null) ||
            body?.message ||
            body?.msg ||
            body?.error ||
            e?.message ||
            '加载对话工作流记录失败，请确认后端已建表并配置代理。'
          this.workflowHistoryError = msg.length > 220 ? msg.slice(0, 220) + '…' : msg
        }
      } finally {
        this.loadingWorkflowHistory = false
      }
    },
    openWorkflowSession(p) {
      const uuid = (p && p.uuid) || ''
      if (!uuid) return
      this.$router.push({ name: 'ConversationWorkflow', query: { session: uuid } })
    },
    onDrop(e) {
      this.isDragging = false
      const files = e.dataTransfer?.files
      if (files && files.length) {
        showSuccessToast(`已接收 ${files.length} 个文件（演示）`)
      }
    },
    onPaste(e) {
      const items = e.clipboardData?.items
      if (!items) return
      for (let i = 0; i < items.length; i++) {
        if (items[i].type && items[i].type.indexOf('image') !== -1) {
          showSuccessToast('已粘贴图片（演示）')
          break
        }
      }
    },
    onFeatureTabClick(tab) {
      if (tab && tab.id === 'canvas') {
        this.$router.push({ name: 'FreeCanvasWorkspace' })
        return
      }
      this.activeFeatureTab = (tab && tab.id) || 'seedance'
    },
    onSubmit() {
      const t = (this.prompt || '').trim()
      if (!t) {
        showErrorToast('请先输入创意描述')
        return
      }
      try {
        sessionStorage.setItem('workspaceInitialPrompt', t)
      } catch (e) {
        /* ignore quota / private mode */
      }
      this.$router.push({ name: 'FreeCanvasWorkspace' })
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.creative-hub .scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
}
</style>
